/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { objectIdString } from "@Helpers/schemas/common.schema";

/**
 * One version entry as the details panel lists it: the `fileVersions` slice's own row type plus the
 * display fields `getFileVersions` really returns. SLICE TYPE GAP: the slice declares its rows as
 * `SourceFileVersionData`, but the endpoint answers with `fileVersionListItemSchema`, which extends
 * that shape with `resourceType` / `metadata` / `uploadByData` (fileOperations.schemas.ts:762) —
 * and `statusInReviews`, which the response schema does not model either. Intersecting here keeps
 * store rows assignable while naming what this panel reads.
 */
type FileVersionSummary = NonNullable<RootState["fileVersions"][string]>[number] & {
  statusInReviews?: { name?: string; category?: string };
  resourceType?: string;
  metadata?: { size?: number };
  uploadByData?: { fullName?: string };
};

/** The file details this panel renders, assembled from `getFileVersionData`. */
/**
 * The file details this panel renders, from `getFileVersionData`. NO index signature: with one, every
 * `data.x` read is `unknown`, and a `{data.x}` JSX child then fails as a React child — the resulting
 * error is reported against the whole enclosing expression, which is very hard to trace back.
 */
type FileDetails = {
  engineType?: string;
  revision?: { value?: string };
  status?: boolean;
  errorMessage?: string;
  conversionStatus?: string;
  statusId?: string;
  data?: unknown;
};
import { useTranslation, untranslated } from "@i18n";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { MdRefresh } from "react-icons/md";
import { getStatusBtnStyle } from "@Features/reviews/helpers";
import { getFileVersions, refreshFileManager, saveEngineType } from "@Actions/fileUpload";
import { prepareEDN } from "@Services/utils/helpers";
import { getProjectFoldersPageUrl } from "@Features/projects/utils/projectUrls";
import { getTasksStatusesData } from "@Features/issues/state/bcfData.actions";
import { convertDate } from "@Services/luxonHelper";
import axiosWrapper from "@Services/axiosWrapper";
import {
  CONVERSION_STATUS_MAPPER,
  SOMETHING_WENT_WRONG,
  TOAST_TYPE_ERROR,
  formatBytes,
  BIM_ENGINE,
  CAD_ENGINE,
  enginesDataSource,
  TOAST_TYPE_SUCCESS,
} from "@Services/constants";
import { TiTick } from "react-icons/ti";
import { ImCross } from "react-icons/im";
import { IoAddOutline } from "react-icons/io5";
import { BiEdit } from "react-icons/bi";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { getSignStatusInitialUserInformation } from "@CubeComponents/constants/globalFunctions";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { showToastNotification } from "@Reducers/toastSlice";
import { getEstimateData } from "@Actions/EstimateList";
import { Breadcrumbs, Link } from "@mui/material";
import CopyToClipboard from "@Actions/CopyToClipboard";
import Skeleton from "@mui/material/Skeleton";
import Text from "@CubeComponents/html/Text/Text";
import type { GetFileVersionDataResponse } from "@Types/fileOperations";
import type { GetFileVersionDataRequest } from "@ServerTypes/fileOperations/fileOperations.types.js";
import type { UpdateFileRevisionResponse, RetryConversionResponse } from "@ServerTypes/fileUpload/fileUpload.schema";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { RootState } from "@Reducers/index";

/** One row of `enginesDataSource`, which is what `SelectDropDown` hands back on selection. */
type EngineOption = (typeof enginesDataSource)[number];

const REVISION_NOT_STARTED = "REVISION_NOT_STARTED";
const REVISION_UPDATING = "REVISION_UPDATING";
const REVISION_CREATING = "REVISION_CREATING";

function DetailsContent(props: {
  containerType?: string;
  disableEngineType?: boolean;
  folderCreatedDate?: string | number;
  folderModifiedDate?: string | number;
  folderSize?: string | number;
  getResourcesUnderWhichFilesShared?: boolean;
  id?: string;
  indexAtProjectLevel?: string | number;
  name?: string;
  parentIdArray?: string[];
  /** Breadcrumb path STRING, per `directoryDataFileItemSchema`. */
  path?: string;
  projectCustomId?: string | number;
  projectId?: string;
  resourceId?: string;
  resourceType?: string;
  revision?: string;
  /** Widened to what `Estimation/Details` passes (its own `SelectedFile`); only these are read. */
  /** Widened to what `Estimation/Details` passes (its own `SelectedFile`); only these are read. */
  /** Widened to what `Estimation/Details` passes (its own `SelectedFile`); only these are read. */
  /**
   * `Estimation/Details` passes its own `SelectedFile`. The index signature is what makes that
   * assignable: with only optional members and no shared member name, TS's weak-type check rejects
   * it outright. Only the three named fields are read here.
   */
  selectedFileData?: {
    createdDate?: number | string;
    estimateId?: string;
    userName?: string;
    [key: string]: unknown;
  };
  spaceId?: string;
  storage?: string | number;
  userName?: string;
  fileVersionId?: string;
  transmittalVersionId?: string;
  conversionStatus?: string;
  getUserAvatar?: (id: string) => React.ReactNode;
  accessPermission?: Record<string, boolean | undefined> | null;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const portalData = useSelector((state: RootState) => state.portalData);
  const fileVersionsState = useSelector((state: RootState) => state.fileVersions);
  const layout = useSelector((state: RootState) => state.layout);
  const [fileFirstVersionCreatedDate, setFileFirstVersionCreatedDate] = useState("");
  const [fileFirstVersionUpdatedDate, setFileFirstVersionUpdatedDate] = useState("");
  const [dateForTooltip, setDateForToolTip] = useState("");
  const [toolTipCreatedDate, setToolTipCreatedDate] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileSize, setFilSize] = useState("");
  const [fileStorage, setFilStorage] = useState("");
  const revisionRef = useRef<string | null | undefined>(null);
  const [filModified, setFilModified] = useState("");
  const [fileVersions, setFileVersions] = useState<FileVersionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fileId = props.resourceId;
  const containerType = props.containerType;
  const projectCustomId = String(props.projectCustomId);

  const [fileVersionNumber, setFileVersionNumber] = useState(0);
  const [fileRevision, setFileRevision] = useState(props.revision);
  const [revisionState, setRevisionState] = useState(REVISION_NOT_STARTED);
  const { fileVersionId, transmittalVersionId, resourceType } = props;
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const projectData = useSelector((state: RootState) => state.projectData);
  const [data, setData] = useState<FileDetails>({});
  const [signUserInformation, setSignUserInformation] = useState<Record<string, unknown>>(
    getSignStatusInitialUserInformation(fileVersionId!, resourceType!, userDetails, projectData),
  );

  const conversionStatus = data.conversionStatus;
  // `conversionStatus` is a free `z.string()` on the wire, so the mapper is read through a widened
  // view of itself rather than asserting the value is one of its keys; an unmapped status is blank.
  const conversionStatusMessages: Record<string, string> = CONVERSION_STATUS_MAPPER;
  const conversionStatusMessage = conversionStatusMessages[conversionStatus ?? ""] ?? "";

  // Only this file's entry is read below. Held apart from the slice so the effect stops re-running
  // on every unrelated write to `fileVersions` (which would clear `isLoading` for a fetch that has
  // not landed yet).
  const currentFileVersions = fileVersionsState[fileId!];

  useEffect(() => {
    if (currentFileVersions) {
      setIsLoading(false);
      if (
        (
          ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_SHORTCUT_FILE", "RESOURCE_TYPE_EMBED"] as (
            | string
            | undefined
          )[]
        ).includes(resourceType)
      ) {
        setFileVersions(currentFileVersions);
      }
    }
  }, [currentFileVersions, resourceType]);

  useEffect(() => {
    setFileRevision(data.revision?.value);
    revisionRef.current = data.revision?.value;
  }, [data.revision]);

  const init = async () => {
    dispatch(
      getFileVersions({
        resourceType: resourceType!,
        fileId: fileId!,
      }),
    );
    if (resourceType == "RESOURCE_TYPE_ESTIMATE") {
      // `selectedFileData` is a grid row with an open index signature — its `estimateId` carries no
      // guarantee of being an ObjectId, and `getEstimateData` sends it straight on as the `_id` of
      // the estimate to load. Validate instead of branding it.
      const estimateId = objectIdString.safeParse(props.selectedFileData?.estimateId);
      if (estimateId.success) {
        await dispatch(getEstimateData({ estimateId: estimateId.data }));
      }
    } else {
      fetchResourcesUnderWhichFilesShared();
      dispatch(getTasksStatusesData());
    }

    if (
      !(["RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_TRANSMITTAL"] as (string | undefined)[]).includes(resourceType) &&
      fileVersionId
    ) {
      const params: GetFileVersionDataRequest = {
        fileVersionId,
        ...(transmittalVersionId ? { transmittalId: transmittalVersionId, spaceId: props.spaceId } : {}),
      };
      const response = await axiosWrapper.post<GetFileVersionDataResponse>(
        `${getBackendUrl()}/fileOperations/getFileVersionData`,
        params,
      );
      if (response.data.status) {
        setData(response.data.data! as { engineType?: string } as FileDetails);
      }
    }
  };

  useEffect(() => {
    if (fileId) {
      setIsLoading(true);
      init();
    }
  }, [fileId]);

  const fetchResourcesUnderWhichFilesShared = () => {
    // props.getResourcesUnderWhichFilesShared({
    //   resourceType,
    //   fileId,
    // });
  };

  useEffect(() => {
    // TODO: The whole if seems patch - setFileFirstVersionCreatedDate is crashing for folder but fiexed temporarily
    if (fileVersions.length && !(["RESOURCE_TYPE_FOLDER"] as (string | undefined)[]).includes(resourceType)) {
      const createdDate = Number(props.folderCreatedDate);
      const updatedDate = Number(fileVersions[0]!.createdDate);
      const formattedCreateDate = convertDate(Number(createdDate), "dateForText");
      const formattedUpdateDate = convertDate(Number(updatedDate), "dateForText");
      const toolTipDate = convertDate(Number(updatedDate), "dateForTooltip");
      const createdDateToolTip = convertDate(Number(createdDate), "dateForTooltip");
      setFileFirstVersionCreatedDate(formattedCreateDate);
      setFileFirstVersionUpdatedDate(formattedUpdateDate);
      setDateForToolTip(toolTipDate);
      setToolTipCreatedDate(createdDateToolTip);
      setFileName(fileVersions[0]!.fileName!);

      if (fileVersions[0]!.resourceType != "RESOURCE_TYPE_ISSUE") {
        setFilSize(formatBytes(fileVersions[0]!.metadata?.size!));
      }
      setFilModified(fileVersions[0]!.uploadByData?.fullName!);

      setFileVersionNumber(fileVersions.length);
      setSignUserInformation({
        ...signUserInformation,
        status: data.statusId,
        prevStatus: data.statusId,
      });
    } else {
      setFileName(props.name!);
      setFileVersionNumber(0);
      const createdDate = Number(props.folderCreatedDate);
      const formattedCreatedDate = convertDate(Number(createdDate), "dateForText");
      setFileFirstVersionCreatedDate(formattedCreatedDate);
      const toolTipCreatedDate = convertDate(Number(createdDate), "dateForTooltip");
      setToolTipCreatedDate(toolTipCreatedDate);
      const updateDate = Number(props.folderModifiedDate || props.folderCreatedDate);
      const formattedUpdateDate = convertDate(Number(updateDate), "dateForText");
      const toolTipUpdatedDate = convertDate(Number(updateDate), "dateForTooltip");
      setFilModified(props.userName!);
      if (props.resourceType == "RESOURCE_TYPE_ESTIMATE") {
        const formattedCreateDate = convertDate(Number(props.selectedFileData!.createdDate), "dateForText");
        setFileFirstVersionCreatedDate(formattedCreateDate);
        setToolTipCreatedDate(convertDate(Number(props.selectedFileData!.createdDate), "dateForTooltip"));
        setFilModified(props.selectedFileData!.userName!);
      }
      setFileFirstVersionUpdatedDate(formattedUpdateDate);
      setDateForToolTip(toolTipUpdatedDate);
      setFilSize(formatBytes(Number(props.folderSize)));
      setFilStorage(formatBytes(Number(props.storage)));
    }
  }, [fileVersions, data.statusId, props.id]);

  const handleEngineSelect = async (selected: EngineOption | EngineOption[] | null) => {
    // `multiple` is not set on the dropdown below, so the array arm never occurs in practice.
    const engineType = Array.isArray(selected) ? selected[0]?.value : selected?.value;
    const res = await saveEngineType({
      fileId: fileId!,
      engineType,
    });
    if (res.data.status) {
      setData((prev: FileDetails) => ({
        ...prev,
        engineType,
      }));
      dispatch(refreshFileManager(true));
      // No `type` key, matching the success toast this has always raised: the reducer's defaults
      // supply it, and passing `type: undefined` would overwrite them.
      dispatch(showToastNotification({ content: untranslated(`Engine ${engineType} set successfully!`) }));
    } else {
      // A toast with no content is dropped by the reducer, so the optional server message needs a
      // fallback rather than an assertion that it is present.
      dispatch(
        showToastNotification({
          content: untranslated(res.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  function BreadcrumbComponent({
    breadcrumbItems = [],
    commonLink,
  }: {
    /** A path split into segments: `.map`-ed, and each item rendered directly as a React child. */
    breadcrumbItems?: string[];
    commonLink?: string;
  }) {
    if (breadcrumbItems.length === 0) {
      return (
        <Link
          href={getProjectFoldersPageUrl(props.projectId!, props.spaceId, props.parentIdArray)}
          color="inherit"
          underline="none"
        >
          <div className="text-sm">
            <Text>/</Text>
          </div>
        </Link>
      );
    }

    return (
      <Breadcrumbs aria-label="breadcrumb" className="text-sm text-black " maxItems={2}>
        {breadcrumbItems.map((item: string, index: number) => (
          <Link key={index} href={commonLink} color="inherit" underline="none">
            {item}
          </Link>
        ))}
      </Breadcrumbs>
    );
  }

  // Wired to the tick icon below and nowhere else, so it takes the click event it is actually given.
  // It used to be declared over a `{ value, cancel }` dropdown event and open with `e.cancel = true`,
  // which wrote an expando onto the React synthetic event and was read by nothing.
  const handleChangeFileRevision = async () => {
    if (!fileRevision) {
      return;
    }
    const saveData: Record<string, unknown> = {
      fileVersionId,
      revision: fileRevision,
    };
    if (revisionState == REVISION_UPDATING) {
      saveData.action = "update";
    }
    const res = await axiosWrapper.post<UpdateFileRevisionResponse>(
      `${getBackendUrl()}/fileUpload/updateFileRevision`,
      saveData,
    );
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Revison saved successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      dispatch(
        getFileVersions({
          resourceType: resourceType!,
          fileId: fileId!,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(res.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setRevisionState(REVISION_NOT_STARTED);
  };

  const handleCancelFileRevision = () => {
    setFileRevision(revisionRef.current!);
    setRevisionState(REVISION_NOT_STARTED);
  };

  const handleAddRevision = () => {
    revisionRef.current = fileRevision;
    setRevisionState(REVISION_CREATING);
  };

  const handleEditRevision = () => {
    if (props.accessPermission?.write) {
      revisionRef.current = fileRevision;
      setRevisionState(REVISION_UPDATING);
    }
  };

  const handleConversionStatusRetry = () => {
    if (!props.accessPermission?.write) {
      dispatch(
        showToastNotification({
          content: t("Insufficient permission to perform this action."),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }
    const params = {
      fileVersionId,
    };
    axiosWrapper.post<RetryConversionResponse>(`${getBackendUrl()}/fileUpload/retryConversion`, params);
  };

  const navigateToFolderLocationOfFile = () => {
    navigate(getProjectFoldersPageUrl(props.projectId!, props.spaceId, props.parentIdArray));
  };

  const getStatusDetails = () => {
    if (fileVersions.length) {
      const statusInReviews = fileVersions.slice(-1)[0]!.statusInReviews;

      if (!statusInReviews?.name) {
        return "-";
      }

      const category = statusInReviews.category || null;

      const style = getStatusBtnStyle(category);

      return (
        <div className="w-fit rounded px-4 py-1 text-sm text-white" style={style}>
          <p>
            <Text>{statusInReviews.name}</Text>
          </p>
        </div>
      );
    }

    return "-";
  };
  return (
    <>
      {!isLoading && (
        <div className="px-3">
          <div className="mb-4 flex items-center" data-testid="fileNameWrapper">
            <div className="flex-[2] text-sm font-semibold" data-testid="fileNameLabel">
              <Text>{t("Name")}</Text>
            </div>
            <div className="flex-[2] truncate text-sm" data-testid="fileName">
              <Text>{fileName}</Text>
            </div>
          </div>

          {/* Link + Location point at the file's source space. For external transmittal references
              the recipient has no access to that space, so the URL/breadcrumb would be a dead/403
              link — suppress both affordances in the external context. */}
          {
            <>
              <div className="mb-4 flex items-center" data-testid="fileLinkWrapper">
                <div className="flex-[2] text-sm font-semibold" data-testid="fileLinkLabel">
                  <Text>{t("Link")}</Text>
                </div>
                <div className="flex-[2] truncate text-sm" style={{ marginInlineStart: "1rem" }} data-testid="fileLink">
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.TEXT_BUTTON}
                    type="button"
                    onClick={navigateToFolderLocationOfFile}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key.toLowerCase() === "e") e.preventDefault();
                    }}
                    className="overflow-hidden text-ellipsis"
                    aria-label="Open folder location"
                  >
                    {portalData.frontendUrl +
                      getProjectFoldersPageUrl(props.projectId!, props.spaceId, props.parentIdArray)}
                  </ButtonComponent>
                </div>
                <div>
                  <CopyToClipboard
                    text={
                      portalData.frontendUrl +
                      getProjectFoldersPageUrl(props.projectId!, props.spaceId, props.parentIdArray)
                    }
                  />
                </div>
              </div>
              <div className="mb-4 flex items-center" data-testid="fileLocationWrapper">
                <div className="flex-[2] text-sm font-semibold" data-testid="fileLocationLabel">
                  <Text>{t("Location")}</Text>
                </div>
                <div className="flex-[2] truncate text-sm" data-testid="fileLocation">
                  <div className="cursor-pointer overflow-x-auto text-[0.25rem] font-semibold flex-[2]">
                    <BreadcrumbComponent
                      // `props.path` is a breadcrumb path STRING (`directoryDataFileItemSchema.path` is
                      // `z.string()`) while `BreadcrumbComponent` maps over segments, so it is split here.
                      // It used to be asserted to `string & string[]` — a type nothing can inhabit — which
                      // made `breadcrumbItems.map is not a function` a render-time crash the moment a
                      // non-empty path arrived. Today `getDirectoryDataOfFile` does not project `path`, so
                      // this stays `[]` and renders exactly as before.
                      breadcrumbItems={props.path ? props.path.split("/").filter(Boolean) : []}
                      commonLink={getProjectFoldersPageUrl(props.projectId!!, props.spaceId, props.parentIdArray)}
                    />
                  </div>
                </div>
              </div>
            </>
          }
          {(["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_SHORTCUT_FILE"] as (string | undefined)[]).includes(resourceType) ? (
            <div className="mb-4 flex items-center" data-testid="fileSizeWrapper">
              <div className="flex-[2] text-sm font-semibold" data-testid="fileSizeLabel">
                <Text>{t("Size")}</Text>
              </div>
              <div className="flex-[2] truncate text-sm" data-testid="fileSize">
                <Text>{fileSize}</Text>
              </div>
            </div>
          ) : null}

          {["RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FILE", "RESOURCE_TYPE_SHORTCUT_FILE"].includes(
            resourceType!,
          ) && (
            <div className="mb-4 flex items-center" data-testid="fileTypeWrapper">
              <div className="flex-[2] text-sm font-semibold" data-testid="fileTypeLabel">
                <Text>{t("File type")}</Text>
              </div>
              <div className="flex-[2] truncate text-sm" data-testid="fileType">
                {containerType?.toUpperCase() || ""}
              </div>
            </div>
          )}

          {(["RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FILE"] as (string | undefined)[]).includes(resourceType) && (
            <div className="mb-4 flex items-center">
              <div className="flex-[2] text-sm font-semibold" data-testid="fileIDLabel">
                <Text> </Text>
                <Text>{"File ID"}</Text>
                <Text> </Text>
              </div>
              <div className="flex-[2] truncate text-sm" style={{ marginInlineStart: "1rem" }} data-testid="fileID">
                <ToolTipComponent
                  showTipPointer
                  position="top"
                  // @ts-expect-error -- `prepareEDN` requires `string | number` but the project custom-id / index it is handed are optional on their source rows.
                  content={untranslated(prepareEDN(projectCustomId, props.indexAtProjectLevel))}
                >
                  <div className="flex-[2] truncate text-sm">
                    {/* @ts-expect-error -- same as line 541: optional id into `prepareEDN`'s required parameter. */}
                    <Text>{prepareEDN(projectCustomId, props.indexAtProjectLevel)}</Text>
                  </div>
                </ToolTipComponent>
              </div>
              <div data-testid="fileIDCopyIcon">
                {/* @ts-expect-error -- same as line 541: optional id into `prepareEDN`'s required parameter. */}
                <CopyToClipboard text={prepareEDN(projectCustomId, props.indexAtProjectLevel)} />
              </div>
            </div>
          )}

          {(["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE"] as (string | undefined)[]).includes(resourceType) && (
            <div className="mb-4 flex items-center" data-testid="engineDetailsWrapper">
              <div className="flex-[2] text-sm font-semibold" data-testid="engineLabel">
                <Text>{t("Engine")}</Text>
              </div>
              <div className="flex-[2] text-sm" data-testid="engineDropdown">
                {props.disableEngineType ? (
                  data.engineType
                ) : (
                  <SelectDropDown
                    disabled={!props.accessPermission?.write}
                    // `SelectDropDownProps.value` accepts the bare `fields.value` id, which is all
                    // this panel holds — see the `OptionValue` doc on the component.
                    value={data.engineType}
                    fields={{ text: "name", value: "value" }}
                    options={enginesDataSource}
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onChange={handleEngineSelect}
                  />
                )}
              </div>
            </div>
          )}
          {(
            ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_SHORTCUT_FILE"] as (string | undefined)[]
          ).includes(resourceType) && (
            <div className="mb-4 flex items-center" data-testid="fileStorageWrapper">
              <div className="flex-[2] text-sm font-semibold" data-testid="fileStorageLabel">
                <Text>{t("Storage")}</Text>
              </div>
              <div className="flex-[2] truncate text-sm" data-testid="fileStorage">
                <Text>{fileStorage}</Text>
              </div>
            </div>
          )}

          {(
            ["RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FILE", "RESOURCE_TYPE_EMBED"] as (string | undefined)[]
          ).includes(resourceType) && (
            <div className="mb-4 flex items-center" data-testid="fileVersionWrapper">
              <div className="flex-[2] text-sm font-semibold" data-testid="fileVersionLabel">
                <Text>{t("Version")}</Text>
              </div>
              <div className="flex-[2] truncate text-sm" data-testid="fileVersion">
                <Text>{`V${fileVersionNumber}`}</Text>
              </div>
            </div>
          )}
          {layout.FILE_PROPERTIES?.REVISION?.ENABLE &&
            (["RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FILE"] as (string | undefined)[]).includes(resourceType) && (
              <div className="mb-4 flex items-center" data-testid="fileRevisionWrapper">
                <div className="flex-[2] text-sm font-semibold" data-testid="fileRevisionLabel">
                  <Text>{t("Revision")}</Text>
                </div>
                <div className="flex flex-[2] items-center gap-4 truncate text-sm" data-testid="fileRevision">
                  <TextFieldComponent
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFileRevision(e.target.value);
                    }}
                    type="text"
                    name="revision"
                    value={fileRevision}
                    disabled={
                      ![REVISION_UPDATING, REVISION_CREATING].includes(revisionState) || !props.accessPermission?.write
                    }
                  />
                  {
                    <>
                      {[REVISION_CREATING, REVISION_UPDATING].includes(revisionState) && (
                        <TiTick className="cursor-pointer" onClick={handleChangeFileRevision} size={32} />
                      )}
                      {[REVISION_CREATING, REVISION_UPDATING].includes(revisionState) && (
                        <ImCross className="cursor-pointer" onClick={handleCancelFileRevision} size={20} />
                      )}
                      {revisionState == REVISION_NOT_STARTED && fileRevision && (
                        <BiEdit
                          className={`${props.accessPermission?.write ? "cursor-pointer" : "cursor-not-allowed"}`}
                          onClick={handleEditRevision}
                          size={32}
                        />
                      )}
                      {revisionState == REVISION_NOT_STARTED && (
                        <IoAddOutline className="cursor-pointer" onClick={handleAddRevision} size={32} />
                      )}
                    </>
                  }
                </div>
              </div>
            )}

          <div className="mb-4 flex items-center" data-testid="fileCreatedOnWrapper">
            <div className="flex-[2] text-sm font-semibold" data-testid="fileCreatedOnLabel">
              <Text>{t("Created On")}</Text>
            </div>
            <div className="flex-[2] truncate text-sm" data-testid="fileCreatedOn">
              <div style={{ width: "fit-content" }}>
                <ToolTipComponent position="top" content={untranslated(toolTipCreatedDate)}>
                  {fileFirstVersionCreatedDate}
                </ToolTipComponent>
              </div>
            </div>
          </div>

          {resourceType != "RESOURCE_TYPE_ESTIMATE" && (
            <div className="mb-4 flex items-center" data-testid="fileModifiedByWrapper">
              <div className="flex-[2] text-sm font-semibold" data-testid="fileModifiedByLabel">
                <Text>{t("Modified by")}</Text>
              </div>
              <div style={{ flex: "2", fontSize: "0.875rem" }} data-testid="fileModifiedBy">
                <Text>{filModified}</Text>
              </div>
            </div>
          )}

          {resourceType != "RESOURCE_TYPE_ESTIMATE" && (
            <div className="mb-4 flex items-center" data-testid="fileModifiedOnWrapper">
              <div className="flex-[2] text-sm font-semibold" data-testid="fileModifiedOnLabel">
                <Text>{t("Modified On")}</Text>
              </div>
              <div style={{ flex: "2", fontSize: "0.875rem" }} data-testid="fileModifiedOn">
                <div style={{ width: "fit-content" }}>
                  <ToolTipComponent position="top" content={untranslated(dateForTooltip)}>
                    {fileFirstVersionUpdatedDate}
                  </ToolTipComponent>
                </div>
              </div>
            </div>
          )}

          {!(["RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_TRANSMITTAL"] as (string | undefined)[]).includes(
            resourceType,
          ) && (
            <div className="mb-4 flex items-center" data-testid="fileStatuswrapper">
              <div className="flex-[2] text-sm font-semibold" data-testid="fileStatusLabel">
                <Text>{t("Status")}</Text>
              </div>
              <div className="flex-[2] overflow-x-auto text-sm" data-testid="fileStatus">
                {getStatusDetails() as React.ReactNode}
              </div>
            </div>
          )}

          {(["RESOURCE_TYPE_FILE"] as (string | undefined)[]).includes(resourceType) &&
            ([BIM_ENGINE, CAD_ENGINE] as (string | undefined)[]).includes(data.engineType) &&
            conversionStatus && (
              <div className="mb-4 flex items-center" data-testid="conversionStatusWrapper">
                <div className="flex-[2] text-sm font-semibold" data-testid="conversionStatusLabel">
                  <Text>{t("Conversion Status")}</Text>
                </div>
                <div className={`flex-2 flex items-center gap-1`} data-testid="conversionStatus">
                  <span className=" whitespace-nowrap overflow-hidden text-ellipsis">
                    <Text>{conversionStatusMessage}</Text>
                  </span>
                  {(conversionStatus === "ERRORED" || conversionStatus === "COMPLETED") && (
                    <ToolTipComponent className="flex" showTipPointer position="top" content={t("Retry")}>
                      <MdRefresh className="cursor-pointer" onClick={handleConversionStatusRetry} />
                    </ToolTipComponent>
                  )}
                </div>
              </div>
            )}

          {(["RESOURCE_TYPE_FOLDER"] as (string | undefined)[]).includes(resourceType) ? (
            <div className="mb-4 flex items-center" data-testid="sharedToSpaceWrapper">
              <div className="flex-[2] text-sm font-semibold" data-testid="sharedToSpaceLabel">
                <Text>{t("Shared to space")}</Text>
              </div>
              <div className="flex-[2] truncate text-sm" data-testid="sharedToSpace">
                <Text>-</Text>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {isLoading && (
        <div className="px-3">
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
        </div>
      )}
    </>
  );
}

export default DetailsContent;
